import {
  DIFFICULTIES,
  type Difficulty,
  type LoadedSongPack,
  type ManifestV1,
} from '../songpack/index.js';

/**
 * What the library knows about a song without opening it.
 *
 * This record duplicates fields that also live in `manifest.json`, and that is a
 * deliberate trade rather than an oversight. Song Select has to draw a list
 * before anything is decoded; reading it from the manifests would mean pulling
 * every pack's manifest out of storage on every launch, and reading it from the
 * archives would mean inflating them. So the library keeps a **projection**:
 *
 *   manifest.json   the truth, stored verbatim, never rewritten
 *   SongEntry       a derived index, rebuilt from the manifest whenever a pack
 *                   is written, and never edited on its own
 *
 * The rule that keeps the two from drifting is that `describe()` below is the
 * only thing that ever produces a `SongEntry`. Nothing constructs one by hand,
 * so there is no path by which the index can come to disagree with the pack it
 * describes. If a field here ever contradicts the manifest, the manifest wins
 * and the entry is regenerated.
 *
 * What is *not* duplicated: everything Song Select does not draw. Chart events,
 * beat times, spawn modes, colours and the audio itself stay in the pack.
 */

/** Where a song in the library came from. */
export type SongSource = 'created' | 'imported' | 'builtin';

export interface SongEntry {
  readonly packId: string;
  readonly songId: string;
  readonly displayName: string;
  readonly author: string;
  readonly bpm: number;
  /** Song length in seconds. Measured on the way in; 0 when not yet known. */
  readonly durationSeconds: number;
  /** Event count per difficulty, for the selector's read-out. */
  readonly difficulties: Readonly<Record<Difficulty, number>>;
  readonly hasCover: boolean;
  /** True when the music video travels inside the pack. */
  readonly hasVideo: boolean;
  /**
   * The manifest's `extensions.mvFile`, whatever it named.
   *
   * Kept even when `hasVideo` is false, because a pack may reference a video it
   * does not carry — an external file next to the game — and Song Select should
   * still be able to say so.
   */
  readonly mvReference: string | null;
  readonly source: SongSource;
  /** Milliseconds since the epoch. */
  readonly createdAt: number;
  readonly updatedAt: number;
  /** Bytes each stored asset occupies, for the storage read-out. */
  readonly bytes: Readonly<Record<string, number>>;
}

/** Total stored size of a song, in bytes. */
export function entryBytes(entry: SongEntry): number {
  let total = 0;
  for (const value of Object.values(entry.bytes)) total += value;
  return total;
}

export interface DescribeOptions {
  readonly source: SongSource;
  readonly durationSeconds?: number;
  readonly createdAt?: number;
  readonly bytes?: Readonly<Record<string, number>>;
}

/**
 * Derives the index record from a pack that has already been validated.
 *
 * Takes a `LoadedSongPack` rather than loose fields on purpose: the only way to
 * get one is through `SongPackLoader`, so an entry cannot exist for a pack this
 * runtime has not read.
 */
export function describe(pack: LoadedSongPack, options: DescribeOptions): SongEntry {
  const now = Date.now();
  const difficulties = {} as Record<Difficulty, number>;
  for (const difficulty of DIFFICULTIES) {
    difficulties[difficulty] = pack.charts[difficulty].events.length;
  }
  return {
    packId: pack.manifest.packId,
    songId: pack.manifest.songId,
    displayName: pack.manifest.displayName,
    author: pack.manifest.author,
    bpm: pack.manifest.bpm,
    durationSeconds: options.durationSeconds ?? 0,
    difficulties,
    hasCover: pack.cover !== null,
    hasVideo: pack.videoEntry !== null,
    mvReference: mvReferenceOf(pack.manifest),
    source: options.source,
    createdAt: options.createdAt ?? now,
    updatedAt: now,
    bytes: options.bytes ?? {},
  };
}

function mvReferenceOf(manifest: ManifestV1): string | null {
  const property = manifest.extensions.find((value) => value.key === 'mvFile');
  const reference = property?.value.trim();
  return reference === undefined || reference.length === 0 ? null : reference;
}

/** Newest first, which is what a library of songs you just made should show. */
export function byNewest(left: SongEntry, right: SongEntry): number {
  return right.createdAt - left.createdAt;
}
